import React, { useState } from 'react';
import { Badge, Card, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiArrowDown, FiArrowUp, FiMinus, FiMoreVertical } from 'react-icons/fi';
import { subtleBadgeProps } from '@core/utils/subtleBadge';
import type {
  DashboardFinanceSnapshotDeductible,
  DashboardFinanceSnapshotPeriod,
} from '../types/dashboard';
import {
  financeSnapshotDepartmentReportPath,
  financeSnapshotMembershipPath,
} from '../utils/dashboardPaths';

interface Props {
  periods: DashboardFinanceSnapshotPeriod[];
  scope: string;
  scopeId: number;
}

export const FINANCE_NO_FIGURES_TEXT = 'No figures for this period yet';

const Dd = Dropdown as unknown as React.FC<React.PropsWithChildren<Record<string, unknown>>>;

function formatMoney(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

function periodHasData(period: DashboardFinanceSnapshotPeriod): boolean {
  return period.membership !== null || period.income !== null || (period.deductibles?.length ?? 0) > 0;
}

function TrendIcon({ trend }: { trend: 'up' | 'down' | 'flat' }) {
  if (trend === 'up') {
    return <FiArrowUp className="dashboard-finance-trend dashboard-finance-trend--up" aria-hidden />;
  }
  if (trend === 'down') {
    return <FiArrowDown className="dashboard-finance-trend dashboard-finance-trend--down" aria-hidden />;
  }
  return <FiMinus className="dashboard-finance-trend dashboard-finance-trend--flat" aria-hidden />;
}

interface CaretProps {
  open: boolean;
  label: string;
  controls: string;
  onToggle: () => void;
  className?: string;
}

const CaretToggle: React.FC<CaretProps> = ({ open, label, controls, onToggle, className }) => (
  <button
    type="button"
    className={`dashboard-finance-caret btn btn-link btn-sm ${className ?? ''}`.trim()}
    aria-expanded={open}
    aria-controls={controls}
    onClick={onToggle}
  >
    <span className="dashboard-finance-caret__mark" aria-hidden="true">
      {open ? '\u25BE' : '\u25B8'}
    </span>
    <span>{label}</span>
  </button>
);

interface PeriodKebabProps {
  period: DashboardFinanceSnapshotPeriod;
  scope: string;
  scopeId: number;
}

const PeriodKebab: React.FC<PeriodKebabProps> = ({ period, scope, scopeId }) => {
  const items: Array<{ key: string; label: string; to: string }> = [];
  if (period.links.show_membership) {
    items.push({
      key: 'membership',
      label: 'View membership',
      to: financeSnapshotMembershipPath(scope, scopeId),
    });
  }
  period.links.departments.forEach((dept) => {
    items.push({
      key: `dept-${dept.department_id}`,
      label: `View ${dept.department_name} department report`,
      to: financeSnapshotDepartmentReportPath(
        scope,
        scopeId,
        period.report_tag_id,
        dept.department_id
      ),
    });
  });

  if (items.length === 0) {
    return null;
  }

  return (
    <Dd className="dashboard-finance-kebab" align="end">
      <Dropdown.Toggle
        variant="outline-secondary"
        size="sm"
        aria-label={`Actions for ${period.period_name}`}
        className="dashboard-finance-kebab__toggle"
      >
        <FiMoreVertical aria-hidden />
      </Dropdown.Toggle>
      <Dropdown.Menu
        renderOnMount
        popperConfig={{
          strategy: 'fixed',
          modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } }],
        }}
      >
        {items.map((item) => (
          <Dropdown.Item as={Link} key={item.key} to={item.to} className="d-flex align-items-center gap-2">
            {item.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dd>
  );
};

interface DeductibleBlockProps {
  deductible: DashboardFinanceSnapshotDeductible;
  defaultOpen: boolean;
  bodyId: string;
}

const DeductibleBlock = ({ deductible, defaultOpen, bodyId }: DeductibleBlockProps): React.ReactElement => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="dashboard-finance-nested">
      <div className="dashboard-finance-row dashboard-finance-row--nested-head">
        <CaretToggle
          open={open}
          label={deductible.name || deductible.code || `Deductible ${deductible.deductible_id}`}
          controls={bodyId}
          onToggle={() => setOpen((value) => !value)}
        />
        <span className="dashboard-finance-row__value">{formatMoney(deductible.total)}</span>
      </div>
      {open ? (
        <div id={bodyId} className="dashboard-finance-nested__body">
          {deductible.breakdown.map((row) => (
            <div key={row.kpi_id} className="dashboard-finance-row dashboard-finance-row--child">
              <span className="dashboard-finance-row__label">{row.kpi_name}</span>
              <span className="dashboard-finance-row__value">{formatMoney(row.amount)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

interface PeriodCardProps {
  period: DashboardFinanceSnapshotPeriod;
  scope: string;
  scopeId: number;
}

/** Full period figures, shown under a tile's Details caret. */
const PeriodDetails = ({ period }: Pick<PeriodCardProps, 'period'>): React.ReactElement => {
  const hasData = periodHasData(period);
  const sectionsDefaultOpen = period.is_latest && hasData;
  const [membershipOpen, setMembershipOpen] = useState(sectionsDefaultOpen);
  const [incomeOpen, setIncomeOpen] = useState(sectionsDefaultOpen);
  const [deductiblesOpen, setDeductiblesOpen] = useState(sectionsDefaultOpen);
  const [otherOpen, setOtherOpen] = useState(false);

  const bodyId = `finance-period-${period.report_tag_id}`;
  const membershipId = `${bodyId}-membership`;
  const incomeId = `${bodyId}-income`;
  const deductiblesId = `${bodyId}-deductibles`;
  const otherId = `${bodyId}-other`;

  return (
    <div className="dashboard-finance-period">
        <div id={bodyId} className="dashboard-finance-period__body">
          {!hasData ? (
            <p className="dashboard-finance-period__empty mb-0" role="status">
              {FINANCE_NO_FIGURES_TEXT}
            </p>
          ) : (
            <div className="dashboard-finance-sections">
              {period.membership ? (
                <section className="dashboard-finance-section">
                  <CaretToggle
                    open={membershipOpen}
                    label="Membership"
                    controls={membershipId}
                    onToggle={() => setMembershipOpen((value) => !value)}
                    className="dashboard-finance-caret--section"
                  />
                  {membershipOpen ? (
                    <div id={membershipId} className="dashboard-finance-ledger">
                      <div className="dashboard-finance-row">
                        <span className="dashboard-finance-row__label">Membership this month</span>
                        <span className="dashboard-finance-row__value">
                          {formatCount(period.membership.this_month)}
                          {period.membership.trend ? <TrendIcon trend={period.membership.trend} /> : null}
                        </span>
                      </div>
                      <div className="dashboard-finance-row">
                        <span className="dashboard-finance-row__label">Membership last month</span>
                        <span className="dashboard-finance-row__value">
                          {period.membership.last_month === null
                            ? 'n/a'
                            : formatCount(period.membership.last_month)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {period.income ? (
                <section className="dashboard-finance-section">
                  <CaretToggle
                    open={incomeOpen}
                    label="Church income"
                    controls={incomeId}
                    onToggle={() => setIncomeOpen((value) => !value)}
                    className="dashboard-finance-caret--section"
                  />
                  {incomeOpen ? (
                    <div id={incomeId} className="dashboard-finance-ledger">
                      {period.income.tithes !== null ? (
                        <div className="dashboard-finance-row">
                          <span className="dashboard-finance-row__label">Tithes</span>
                          <span className="dashboard-finance-row__value">
                            {formatMoney(period.income.tithes)}
                          </span>
                        </div>
                      ) : null}
                      {period.income.other_allocated_income !== null ? (
                        <div className="dashboard-finance-nested">
                          <div className="dashboard-finance-row dashboard-finance-row--nested-head">
                            <CaretToggle
                              open={otherOpen}
                              label="Other allocated income"
                              controls={otherId}
                              onToggle={() => setOtherOpen((value) => !value)}
                            />
                            <span className="dashboard-finance-row__value">
                              {formatMoney(period.income.other_allocated_income)}
                            </span>
                          </div>
                          {otherOpen ? (
                            <div id={otherId} className="dashboard-finance-nested__body">
                              {period.income.other_breakdown.map((row) => (
                                <div key={row.kpi_id} className="dashboard-finance-row dashboard-finance-row--child">
                                  <span className="dashboard-finance-row__label">{row.kpi_name}</span>
                                  <span className="dashboard-finance-row__value">
                                    {formatMoney(row.amount)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              ) : null}

              {period.deductibles && period.deductibles.length > 0 ? (
                <section className="dashboard-finance-section">
                  <CaretToggle
                    open={deductiblesOpen}
                    label="Deductibles"
                    controls={deductiblesId}
                    onToggle={() => setDeductiblesOpen((value) => !value)}
                    className="dashboard-finance-caret--section"
                  />
                  {deductiblesOpen ? (
                    <div id={deductiblesId} className="dashboard-finance-ledger">
                      {period.deductibles.map((deductible) => (
                        <div key={`deductible-${deductible.deductible_id}`}>
                          <DeductibleBlock
                            deductible={deductible}
                            defaultOpen={false}
                            bodyId={`${deductiblesId}-${deductible.deductible_id}`}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>
              ) : null}
            </div>
          )}
        </div>
    </div>
  );
};

function tileValue(value: number | null | undefined, format: (v: number) => string): string {
  return value === null || value === undefined ? '—' : format(value);
}

function periodStatusBadge(status: string): { label: string; variant: string } {
  const upper = status.toUpperCase();
  if (upper === 'CLOSED') return { label: 'Closed', variant: 'secondary' };
  if (upper === 'PENDING') return { label: 'Pending', variant: 'info' };
  return { label: 'Open', variant: 'success' };
}

const PeriodTile = ({ period, scope, scopeId }: PeriodCardProps): React.ReactElement => {
  const hasData = periodHasData(period);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const badge = periodStatusBadge(period.status);
  const detailsId = `finance-tile-${period.report_tag_id}`;

  return (
    <div className={`dashboard-finance-tile${detailsOpen ? ' dashboard-finance-tile--open' : ''}`}>
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="min-w-0">
          <div className="dashboard-finance-tile__name">{period.period_name}</div>
          <Badge {...subtleBadgeProps(badge.variant)}>{badge.label}</Badge>
        </div>
        <PeriodKebab period={period} scope={scope} scopeId={scopeId} />
      </div>
      {!hasData ? (
        <p className="dashboard-finance-tile__empty text-muted mb-0" role="status">
          {FINANCE_NO_FIGURES_TEXT}
        </p>
      ) : (
        <>
          <dl className="dashboard-finance-tile__figures mb-0">
            <div>
              <dt>Tithes</dt>
              <dd>{tileValue(period.teaser.tithes, formatMoney)}</dd>
            </div>
            <div>
              <dt>Other income</dt>
              <dd>{tileValue(period.teaser.other_income, formatMoney)}</dd>
            </div>
            <div>
              <dt>Members</dt>
              <dd>{tileValue(period.teaser.members, formatCount)}</dd>
            </div>
          </dl>
          <CaretToggle
            open={detailsOpen}
            label={detailsOpen ? 'Hide details' : 'Details'}
            controls={detailsId}
            onToggle={() => setDetailsOpen((value) => !value)}
          />
          {detailsOpen ? (
            <div id={detailsId}>
              <PeriodDetails period={period} />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

const FinanceSnapshot: React.FC<Props> = ({ periods, scope, scopeId }) => {
  if (!periods.length) return null;

  return (
      <Card className="ntcog-card dashboard-section-card dashboard-finance-card">
        <Card.Header>
          <div>Finance snapshot</div>
          <small className="text-muted fw-normal d-block">
            Approved figures for open periods and the last two closed periods
          </small>
        </Card.Header>
        <Card.Body>
          <div className="dashboard-finance-tiles">
            {periods.map((period) => (
              <div key={`tile-${period.report_tag_id}`} className="dashboard-finance-tile-slot">
                <PeriodTile period={period} scope={scope} scopeId={scopeId} />
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
  );
};

export default FinanceSnapshot;
